import { getFileURL } from '@/features/files/api'
import type { FileType } from '@/features/files/types'
import type { I18nTType, TranslationKeysType } from '@/lib/i18n'
import type { ToastType } from '@/types'
import { convertFileToBlobAndDownload } from '@/utils/client/download-file'

export const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024 * 1024
export const MAX_FILE_SIZE_LABEL = '1 GB'

export type FileUploadPhaseType = 'client' | 'network' | 'server'

export class FileUploadError extends Error {
  readonly status: number | null
  readonly phase: FileUploadPhaseType
  readonly i18nKey: TranslationKeysType

  constructor(opts: {
    message: string
    status: number | null
    phase: FileUploadPhaseType
    i18nKey: TranslationKeysType
  }) {
    super(opts.message)
    this.name = 'FileUploadError'
    this.status = opts.status
    this.phase = opts.phase
    this.i18nKey = opts.i18nKey
  }
}

function i18nKeyForUploadStatus(status: number): TranslationKeysType {
  if (status === 413) return 'error.upload.tooLarge'
  if (status === 401 || status === 403) return 'error.upload.unauthorized'
  if (status >= 500) return 'error.upload.serverError'
  return 'error.upload.generic'
}

export function uploadFileToPresignedUrl({
  file,
  url,
  onProgress,
  onXhrReady,
}: {
  file: File
  url: string
  onProgress?: (percentage: number) => void
  onXhrReady?: (xhr: XMLHttpRequest) => void
}): Promise<void> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return Promise.reject(
      new FileUploadError({
        message: `File too large: ${file.size} bytes (max ${MAX_FILE_SIZE_BYTES})`,
        status: null,
        phase: 'client',
        i18nKey: 'error.upload.tooLarge',
      }),
    )
  }

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Access-Control-Allow-Origin', '*')
    xhr.setRequestHeader('x-amz-acl', 'public-read')
    xhr.setRequestHeader('Content-Type', file.type)

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      })
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve()
      } else {
        reject(
          new FileUploadError({
            message: `Upload failed with status ${xhr.status} ${xhr.statusText || ''}`.trim(),
            status: xhr.status,
            phase: 'server',
            i18nKey: i18nKeyForUploadStatus(xhr.status),
          }),
        )
      }
    }

    xhr.onerror = () => {
      reject(
        new FileUploadError({
          message: 'Network error during upload',
          status: null,
          phase: 'network',
          i18nKey: 'error.upload.network',
        }),
      )
    }

    xhr.onabort = () => {
      reject(
        new FileUploadError({
          message: 'Upload aborted',
          status: null,
          phase: 'client',
          i18nKey: 'error.upload.aborted',
        }),
      )
    }

    if (onXhrReady) onXhrReady(xhr)
    xhr.send(file)
  })
}

export function toastFileUploadError(error: unknown, toast: ToastType | undefined, t: I18nTType) {
  if (error instanceof FileUploadError) {
    if (error.phase === 'client' && error.i18nKey === 'error.upload.aborted') return
    toast?.error(t(error.i18nKey, { maxSize: MAX_FILE_SIZE_LABEL }))
    return
  }
  toast?.error(t('error.common'))
}

// download file
export async function downloadFile({
  userId,
  file,
  toast,
  t,
}: {
  userId: string
  file: FileType
  toast: ToastType | undefined
  t: I18nTType
}) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    if (!file.id || !userId) {
      throw new Error('No file id / user id found')
    }

    toast?.success(t('session.deliverable.downloadStarted'))

    // Show a second message after 3 seconds if download is still in progress
    timeoutId = setTimeout(() => {
      toast?.success(t('session.deliverable.downloadMayTakeTime'))
    }, 3000)

    const data = await getFileURL({
      userId,
      fileId: file.id,
    })
    const url = data.url
    await convertFileToBlobAndDownload(url, file.name)

    // Clear the timeout if download completes before 3 seconds
    if (timeoutId) clearTimeout(timeoutId)

    // Show success message when download is complete
    toast?.success(t('session.deliverable.downloadComplete'))
  } catch (e) {
    // Clear the timeout on error
    if (timeoutId) clearTimeout(timeoutId)

    console.log(e)
    toast?.error(t('error.common'))
    throw e // Re-throw to allow component to handle error
  }
}
